import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import { Avatar } from "@govlaunch/web";
import Link from "next/link";
import React, { SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useToggle } from "react-use";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import ArchiveProduct from "~/lib/mutations/ArchiveProduct";
import ToggleEnrichedProduct from "~/lib/mutations/ToggleEnrichedProduct";
import {
  IArchiveProductMutation,
  IArchiveProductMutationVariables,
} from "~/lib/mutations/__generated__/ArchiveProduct.generated";
import {
  IToggleEnrichedProductMutation,
  IToggleEnrichedProductMutationVariables,
} from "~/lib/mutations/__generated__/ToggleEnrichedProduct.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { IPlanTier } from "~/types/types";
import BoltIcon from "~/components/icons/BoltIcon";
import GovernmentIcon from "~/components/icons/GovernmentIcon";
import MakersIcon from "~/components/icons/MakersIcon";
import { Pencil } from "~/components/product/icons";
import { Margin } from "~/components/spacings";
import Dotdotdot from "~/components/text/Dotdotdot";
import Tooltip from "~/components/Tooltip";
import CounterIcon from "~/components/vendor/profile/CounterIcon";
import UpgradeAccountBannerModal from "~/components/vendor/profile/UpgradeAccountBannerModal";

interface IVendorProducts {
  vendor: NonNullable<IVendorQuery["company"]>;
}

export default function VendorProducts({ vendor }: IVendorProducts) {
  const intl = useIntl();
  const [isModalOpen, toggleModalOpen] = useToggle(false);

  const enrichedProducts = (vendor.products || []).filter(({ enrichedProfile }) => enrichedProfile);
  const hasReachedMaximumEnrichedProducts = enrichedProducts.length >= vendor.plan.limits.enrichedProducts;
  const isVendorBasic = vendor.plan.tier === IPlanTier.Basic;
  const isVendorMakers = vendor.plan.tier === IPlanTier.Makers;
  const isVendorMakersPro = vendor.plan.tier === IPlanTier.Makerspro;

  return (
    <div
      css={{
        display: "grid",
        gridAutoFlow: "row",
        gridRowGap: 1,
        overflow: "hidden",
        backgroundColor: palette.lightestGray,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 5,
      }}
    >
      <UpgradeAccountBannerModal isOpen={isModalOpen} toggleOpen={toggleModalOpen} isVendorBasic={isVendorBasic} />

      {(vendor.products || []).map((product) => (
        <div
          key={product._id}
          css={{
            position: "relative",
            display: "flex",
            flexDirection: "row",
            padding: 12,
            backgroundColor: palette.white,
            ":hover": {
              backgroundColor: palette.washGray,
            },
          }}
        >
          {vendor.viewerIsMember && (
            <div
              css={{
                position: "absolute",
                top: product.isDisruptor ? 12 : 8,
                right: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              {!product.isDisruptor && (
                <Tooltip
                  backgroundColor={palette.innovatorBlue}
                  boxStyle={
                    hasReachedMaximumEnrichedProducts && !product.enrichedProfile && !isVendorBasic
                      ? {
                          maxWidth: 250,
                          padding: 8,
                          textAlign: "center",
                        }
                      : {
                          display: "none",
                        }
                  }
                  render={() =>
                    hasReachedMaximumEnrichedProducts && !product.enrichedProfile ? (
                      <span
                        css={{
                          color: palette.white,
                          fontSize: 14,
                          textAlign: "center",
                        }}
                      >
                        <FormattedMessage
                          defaultMessage="You have reached the maximum number of {plan} products"
                          id="dEGlYB"
                          values={{ plan: isVendorMakers ? "Makers" : "Makers Pro" }}
                        />{" "}
                        <br />{" "}
                        <span
                          css={{
                            fontWeight: 600,
                          }}
                        >
                          <FormattedMessage defaultMessage="Click to Upgrade" id="15Ij0X" />
                        </span>
                      </span>
                    ) : null
                  }
                >
                  {({ innerRef, bind }) => (
                    <Mutation<IToggleEnrichedProductMutation, IToggleEnrichedProductMutationVariables>
                      mutation={ToggleEnrichedProduct}
                      refetchQueries={refetchQueries(["CompanyAccountBillingInfo"])}
                    >
                      {(toggleEnrichedProduct) => {
                        if (isVendorMakersPro && hasReachedMaximumEnrichedProducts && !product.enrichedProfile) {
                          return null;
                        }

                        return (
                          <button
                            onClick={() => {
                              if (!product.enrichedProfile && hasReachedMaximumEnrichedProducts) {
                                toggleModalOpen();

                                return;
                              }

                              toggleEnrichedProduct({
                                variables: {
                                  productId: product._id,
                                },
                              });
                            }}
                            {...bind}
                            ref={innerRef}
                            type="button"
                            css={{
                              cursor: "pointer",
                              border: `1px solid ${palette.lightestGray}`,
                              background: "#fff",

                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              borderRadius: 4,
                              padding: 8,
                            }}
                          >
                            {product.enrichedProfile ? (
                              <span
                                css={{
                                  marginRight: 4,
                                  fontWeight: 500,
                                }}
                              >
                                <FormattedMessage defaultMessage="Set to Basic" id="Y4TPbu" />
                              </span>
                            ) : (
                              <span
                                css={{
                                  marginRight: 4,
                                  fontWeight: 500,
                                }}
                              >
                                <FormattedMessage defaultMessage="Set as Makers" id="WcIcFD" />
                              </span>
                            )}
                          </button>
                        );
                      }}
                    </Mutation>
                  )}
                </Tooltip>
              )}

              <Link
                href="/products/[productSlug]/edit"
                as={`/products/${product.slug}/edit`}
                passHref={true}
                legacyBehavior={true}
              >
                <a
                  css={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    marginLeft: 8,
                    minHeight: 36,
                  }}
                  aria-label={intl.formatMessage({
                    defaultMessage: "Edit product",
                    id: "Tk3zCB",
                  })}
                >
                  <Pencil fill="#797979" width={15} height={18} />
                </a>
              </Link>

              <Mutation<IArchiveProductMutation, IArchiveProductMutationVariables>
                mutation={ArchiveProduct}
                awaitRefetchQueries={true}
                refetchQueries={refetchQueries(["CompanyAccountBillingInfo", "Vendor"])}
              >
                {(archiveProduct) => (
                  <Tooltip
                    backgroundColor={palette.text}
                    render={() => (
                      <span
                        css={{
                          display: "block",
                          fontSize: 12,
                          padding: 8,
                          color: palette.white,
                          fontWeight: 500,
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Delete product?",
                          id: "97jX9O",
                        })}
                      </span>
                    )}
                  >
                    {({ innerRef, bind }) => (
                      <button
                        ref={innerRef}
                        {...bind}
                        onClick={() => {
                          if (
                            window.confirm(
                              intl.formatMessage({
                                defaultMessage: "Are you sure you want to remove this product?",
                                id: "LsNq99",
                              }),
                            )
                          ) {
                            archiveProduct({
                              variables: {
                                productId: product._id,
                              },
                            });
                          }
                        }}
                        type="button"
                        css={{
                          marginLeft: 8,
                          cursor: "pointer",
                          padding: 0,
                          display: "inline-flex",
                          border: 0,
                          outline: 0,
                          background: "none",
                          marginRight: 8,
                        }}
                        aria-label={intl.formatMessage({
                          defaultMessage: "Delete product",
                          id: "us+OCh",
                        })}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </Tooltip>
                )}
              </Mutation>
            </div>
          )}

          <Link href={`/products/${product.slug}`} passHref={true} legacyBehavior={true}>
            <a
              css={{
                position: "relative",
              }}
            >
              <Avatar
                size="2xl"
                src={product.logo}
                name={product.name}
                variant={product.isDisruptor ? "disruptor" : null}
              />
              <div
                css={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                }}
              >
                {product.enrichedProfile && !product.isDisruptor && <MakersIcon width={24} height={24} />}
              </div>
            </a>
          </Link>

          <div
            css={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Margin ml={20}>
              <Link href={`/products/${product.slug}`} passHref={true} legacyBehavior={true}>
                <a
                  css={{
                    fontSize: 18,
                    color: palette.text,
                    fontWeight: 600,
                  }}
                >
                  {product.name}
                </a>
              </Link>

              <Margin mt={8} mr={150}>
                <Link href={`/products/${product.slug}`} passHref={true} legacyBehavior={true}>
                  <a
                    css={{
                      fontSize: 14,
                      color: palette.text,
                    }}
                  >
                    <Dotdotdot clamp={3}>{product.description}</Dotdotdot>
                  </a>
                </Link>
              </Margin>

              <Margin mt={8}>
                <Link href={`/products/${product.slug}`} passHref={true} legacyBehavior={true}>
                  <a
                    css={{
                      margin: 0,
                      fontSize: 14,
                      color: palette.text,
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        css={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          columnGap: 10,
                        }}
                      >
                        <CounterIcon
                          count={product.governmentsUsingCount}
                          tooltip={
                            product.governmentsUsingCount && product.governmentsUsingCount > 0
                              ? intl.formatMessage({
                                  defaultMessage: "# Governments using",
                                  id: "zEHwDG",
                                })
                              : intl.formatMessage({
                                  defaultMessage: "No Governments using",
                                  id: "YYklnk",
                                })
                          }
                          width={28}
                          height={28}
                        >
                          <GovernmentIcon width={28} height={28} />
                        </CounterIcon>
                        <CounterIcon
                          count={product.projectsUsingCount}
                          tooltip={
                            product.projectsUsingCount && product.projectsUsingCount > 0
                              ? intl.formatMessage({ defaultMessage: "# Projects using", id: "faMcnu" })
                              : intl.formatMessage({ defaultMessage: "No Projects using", id: "6EypCh" })
                          }
                          width={28}
                          height={28}
                        >
                          <BoltIcon width={28} height={28} />
                        </CounterIcon>
                      </div>
                    </div>
                  </a>
                </Link>
              </Margin>
            </Margin>
          </div>
        </div>
      ))}
    </div>
  );
}

function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width={15} height={18} viewBox="0 0 15 18" {...props}>
      <g fill="#797979">
        <path d="M10.5 6c-.276 0-.5.193-.5.43v8.14c0 .237.224.43.5.43s.5-.193.5-.43V6.43c0-.237-.224-.43-.5-.43zm-6 0c-.276 0-.5.193-.5.43v8.14c0 .237.224.43.5.43s.5-.193.5-.43V6.43C5 6.194 4.776 6 4.5 6z" />
        <path d="M1.228 5.359v10.386c0 .614.231 1.19.635 1.604.401.415.96.65 1.545.651h8.184a2.156 2.156 0 001.545-.651c.404-.414.635-.99.635-1.604V5.359c.802-.208 1.321-.963 1.214-1.765-.107-.802-.808-1.402-1.638-1.402h-2.215v-.527a1.635 1.635 0 00-.499-1.181A1.723 1.723 0 009.42 0H5.58a1.723 1.723 0 00-1.214.484 1.635 1.635 0 00-.5 1.181v.527H1.653c-.83 0-1.53.6-1.638 1.402-.107.802.412 1.557 1.214 1.765zm10.364 11.798H3.408c-.74 0-1.315-.62-1.315-1.412V5.395h10.814v10.35c0 .793-.576 1.412-1.315 1.412zM4.732 1.665a.801.801 0 01.245-.586.845.845 0 01.603-.236h3.84a.845.845 0 01.603.236.8.8 0 01.245.586v.527H4.732v-.527zm-3.08 1.37h11.696c.43 0 .778.34.778.759 0 .42-.348.759-.778.759H1.652a.769.769 0 01-.778-.759c0-.42.348-.759.778-.759z" />
        <path d="M7.5 6c-.276 0-.5.193-.5.43v8.14c0 .237.224.43.5.43s.5-.193.5-.43V6.43C8 6.194 7.776 6 7.5 6z" />
      </g>
    </svg>
  );
}
